import { supabase } from '../lib/supabase';

/**
 * Extracts all mentioned usernames from a text string
 * @param text - The string to extract mentions from
 * @returns - Array of usernames that were mentioned
 */
export const extractMentions = (text: string): string[] => {
  const mentionRegex = /@(\w+)/g;
  const mentions: string[] = [];
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[1]);
  }
  
  return Array.from(new Set(mentions)); // Remove duplicates using Array.from()
};

/**
 * Finds user IDs for all mentioned usernames
 * @param mentions - Array of usernames
 * @returns - Object mapping username to user ID
 */
export const getUserIdsFromMentions = async (mentions: string[]): Promise<Record<string, string>> => {
  if (!mentions.length) return {};
  
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username')
      .in('username', mentions);
    
    if (error) throw error;
    
    return (data || []).reduce((acc, user) => {
      acc[user.username] = user.id;
      return acc;
    }, {} as Record<string, string>);
  } catch (error) {
    console.error('Error getting user IDs from mentions:', error);
    return {};
  }
};

/**
 * Creates notifications for mentioned users
 * @param mentionedUserIds - IDs of mentioned users
 * @param sourceUserId - ID of the user who created the mention
 * @param sourceUsername - Username of the user who created the mention
 * @param referenceId - ID of the content where the mention appears (update or comment)
 * @param isComment - Whether the mention is in a comment
 * @param updateId - If in a comment, the parent update ID
 * @param borrowerName - The name of the borrower related to this mention
 */
export const createMentionNotifications = async (
  mentionedUserIds: string[],
  sourceUserId: string,
  sourceUsername: string,
  referenceId: string,
  isComment: boolean = false,
  updateId?: string,
  borrowerName?: string
): Promise<void> => {
  if (!mentionedUserIds.length) return;
  
  try {
    // Don't notify the user if they mention themselves
    const filteredUserIds = mentionedUserIds.filter(id => id !== sourceUserId);
    if (!filteredUserIds.length) return;
    
    const today = new Date();
    const expiryDate = new Date();
    expiryDate.setDate(today.getDate() + 30); // Expire in 30 days
    
    const notificationType = isComment ? 'comment_mention' : 'update_mention';
    
    // Include borrower name in the message if available
    const borrowerInfo = borrowerName ? ` for ${borrowerName}` : '';
    
    const notifications = filteredUserIds.map(userId => ({
      recipient_id: userId,
      category: 'mention',
      notification_type: notificationType,
      reference_id: isComment ? updateId : referenceId, // For comments, we want to navigate to the parent update
      message: `@${sourceUsername} mentioned you in a ${isComment ? 'comment' : 'post update'}${borrowerInfo}.`,
      is_read: false,
      is_active: true,
      expiry_date: expiryDate.toISOString(),
    }));
    
    const { error } = await supabase
      .from('notifications')
      .insert(notifications);
    
    if (error) throw error;
  } catch (error) {
    console.error('Error creating mention notifications:', error);
  }
};

/**
 * Process mentions in text, extract usernames, get user IDs, and create notifications
 * @param text - Text containing mentions
 * @param sourceUserId - ID of the user who created the mention
 * @param sourceUsername - Username of the user who created the mention
 * @param referenceId - ID of the content where the mention appears (update or comment)
 * @param isComment - Whether the mention is in a comment
 * @param updateId - If in a comment, the parent update ID
 * @param borrowerName - The name of the borrower related to this mention
 * @returns - Array of user IDs who were mentioned
 */
export const processMentionsAndNotify = async (
  text: string,
  sourceUserId: string,
  sourceUsername: string,
  referenceId: string,
  isComment: boolean = false,
  updateId?: string,
  borrowerName?: string
): Promise<string[]> => {
  // Extract mentions
  const mentions = extractMentions(text);
  if (!mentions.length) return [];
  
  // Get user IDs
  const userIdMap = await getUserIdsFromMentions(mentions);
  const mentionedUserIds = Object.values(userIdMap);
  
  // Create notifications
  await createMentionNotifications(
    mentionedUserIds,
    sourceUserId,
    sourceUsername,
    referenceId,
    isComment,
    updateId,
    borrowerName
  );
  
  return mentionedUserIds;
};

/**
 * Creates reminder notifications for mentioned users
 * @param updateId - ID of the update
 * @param reminderDate - Selected reminder date
 * @param sourceUserId - ID of the user who set the reminder
 * @param sourceUsername - Username of the user who set the reminder
 * @param borrowerName - The name of the borrower related to this update
 * @param updateContent - The content of the update to extract mentions from
 * @param collectionId - The collection ID for navigation purposes
 */
export const createReminderNotificationsForMentions = async (
  updateId: string,
  reminderDate: Date,
  sourceUserId: string,
  sourceUsername: string,
  borrowerName: string,
  updateContent: string,
  collectionId: string
): Promise<void> => {
  console.log('Creating reminder notifications for mentions in update:', updateId);
  console.log('Update content:', updateContent);
  
  // Extract mentions from the update content
  const mentions = extractMentions(updateContent);
  console.log('Extracted mentions:', mentions);
  
  if (!mentions.length) {
    console.log('No mentions found in update content');
    return;
  }
  
  try {
    // Get user IDs from the mentions
    const userIdMap = await getUserIdsFromMentions(mentions);
    console.log('User ID map for mentions:', userIdMap);
    
    const mentionedUserIds = Object.values(userIdMap);
    console.log('Mentioned user IDs:', mentionedUserIds);
    
    // Don't notify the user who set the reminder
    const filteredUserIds = mentionedUserIds.filter(id => id !== sourceUserId);
    console.log('Filtered user IDs (excluding source):', filteredUserIds);
    
    if (!filteredUserIds.length) {
      console.log('No users to notify after filtering out source user');
      return;
    }
    
    // Calculate expiry date (30 days after the reminder date)
    const expiryDate = new Date(reminderDate);
    expiryDate.setDate(expiryDate.getDate() + 30);
    
    // Format the reminder date for display
    const formattedDate = reminderDate.toLocaleDateString();
    
    // Create notifications - one for the day before (if applicable) and one for the actual day
    interface ReminderNotification {
      recipient_id: string;
      category: string;
      notification_type: string;
      reference_id: string;
      message: string;
      is_read: boolean;
      is_active: boolean;
      expiry_date: string;
      scheduled_date: string;
    }
    
    const notifications: ReminderNotification[] = [];
    
    // Get today's date for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Create day-before notification
    const dayBefore = new Date(reminderDate);
    dayBefore.setDate(dayBefore.getDate() - 1);
    dayBefore.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Check if day before is today
    const isDayBeforeToday = dayBefore.getTime() === today.getTime();
    
    // Create reminder date for comparison
    const reminderDay = new Date(reminderDate);
    reminderDay.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Check if reminder date is today
    const isReminderToday = reminderDay.getTime() === today.getTime();
    
    // Create day-before notification if applicable
    if (dayBefore >= today) {
      filteredUserIds.forEach(userId => {
        notifications.push({
          recipient_id: userId,
          category: 'reminder',
          notification_type: 'reminder',
          reference_id: updateId,
          message: `@${sourceUsername} set a reminder for "${borrowerName}" for tomorrow (ID: ${collectionId})`,
          is_read: false,
          is_active: isDayBeforeToday, // Active immediately if it's for today
          expiry_date: expiryDate.toISOString(),
          scheduled_date: dayBefore.toISOString(),
        });
      });
    }
    
    // Create day-of notification
    filteredUserIds.forEach(userId => {
      notifications.push({
        recipient_id: userId,
        category: 'reminder',
        notification_type: 'reminder',
        reference_id: updateId,
        message: `@${sourceUsername} set a reminder for "${borrowerName}" for today (ID: ${collectionId})`,
        is_read: false,
        is_active: isReminderToday, // Active immediately if reminder date is today
        expiry_date: expiryDate.toISOString(),
        scheduled_date: reminderDate.toISOString(),
      });
    });
    
    console.log('Creating reminder notifications:', notifications);
    
    if (notifications.length === 0) {
      console.log('No notifications to create');
      return;
    }
    
    const { data, error } = await supabase
      .from('notifications')
      .insert(notifications)
      .select();
    
    if (error) {
      console.error('Error inserting reminder notifications:', error);
      throw error;
    }
    
    console.log('Successfully created reminder notifications:', data);
    console.log(`Created reminder notifications for ${filteredUserIds.length} mentioned users`);
  } catch (error) {
    console.error('Error creating reminder notifications for mentions:', error);
  }
};

/**
 * Activates scheduled reminders that are due today for all users
 * This function should be called by a background process or on app initialization
 */
export const activateGlobalScheduledReminders = async (): Promise<void> => {
  try {
    // Get today's date without time component
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Format today as ISO string for comparison
    const todayStr = today.toISOString().split('T')[0];
    
    console.log('Activating global scheduled reminders for date:', todayStr);
    
    // Activate reminders scheduled for today
    const { data, error } = await supabase
      .from('notifications')
      .update({ is_active: true })
      .eq('category', 'reminder')
      .eq('is_active', false)
      .gte('scheduled_date', new Date(todayStr + 'T00:00:00.000Z').toISOString())
      .lt('scheduled_date', new Date(todayStr + 'T23:59:59.999Z').toISOString())
      .select();
    
    if (error) {
      console.error('Error activating global scheduled reminders:', error);
      throw error;
    }
    
    console.log(`Successfully activated ${data?.length || 0} global scheduled reminders for today`);
    
    // Also check for any reminders that should have been activated in the past but weren't
    const { data: pastData, error: pastError } = await supabase
      .from('notifications')
      .update({ is_active: true })
      .eq('category', 'reminder')
      .eq('is_active', false)
      .lt('scheduled_date', new Date(todayStr + 'T00:00:00.000Z').toISOString())
      .select();
      
    if (pastError) {
      console.error('Error activating past scheduled reminders:', pastError);
    } else {
      console.log(`Also activated ${pastData?.length || 0} past scheduled reminders`);
    }
    
    return;
  } catch (error) {
    console.error('Error in activateGlobalScheduledReminders:', error);
    throw error;
  }
}; 